import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Alert, Empty, Tag, Collapse, Badge } from 'antd';
import { CheckCircleOutlined, WarningOutlined, FileTextOutlined, CameraOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { ITopicPreview } from 'Store/BcfImport/interfaces/IBcfImportState';

interface IStep4SummaryProps {
  fileAnalysisResult: any;
  mappingConfigs: any;
  importSettings: any;
  isLoading: boolean;
  error: string | undefined;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
  border-left: 4px solid ${({ theme }) => theme.colors.primary.primary};
`;

const CardLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

const CardValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0 0 12px 0;
`;

const TopicsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopicCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
`;

const TopicHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const TopicTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  flex: 1;
`;

const TopicMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const TopicDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TopicDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};

  .anticon {
    font-size: 12px;
  }
`;

const LabelsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const MappingSummarySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MappingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
`;

const MappingLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const MappingStatus = styled.span<{ complete: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme, complete }) =>
    complete ? theme.colors.success.success : theme.colors.warning.warning};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SettingsSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 4px;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
`;

const SettingLabel = styled.span`
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

const SettingValue = styled.span`
  color: ${({ theme }) => theme.colors.surface.onSurface};
  font-weight: 500;
`;

const CollapsibleContent = styled.div`
  .ant-collapse {
    border: none;
    background: transparent;
  }
  .ant-collapse-item {
    border: 1px solid ${({ theme }) => theme.colors.surface.surfaceOutline};
    border-radius: 8px !important;
    margin-bottom: 0;
  }
  .ant-collapse-header {
    padding: 12px 16px !important;
    font-size: 13px;
    font-weight: 500;
  }
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const FieldLabels: Record<string, string> = {
  status: 'Status',
  type: 'Tipos',
  priority: 'Prioridades',
  user: 'Usuários',
  stage: 'Etapas',
  labels: 'Disciplinas',
};

const getPriorityColor = (priority?: string): string => {
  if (!priority) return 'default';
  const lower = priority.toLowerCase();
  if (lower.includes('critical') || lower.includes('alta') || lower.includes('high')) return 'red';
  if (lower.includes('medium') || lower.includes('média') || lower.includes('normal')) return 'orange';
  if (lower.includes('low') || lower.includes('baixa')) return 'green';
  return 'default';
};

const getStatusColor = (status?: string): string => {
  if (!status) return 'default';
  const lower = status.toLowerCase();
  if (lower.includes('open') || lower.includes('aberto')) return 'blue';
  if (lower.includes('closed') || lower.includes('fechado') || lower.includes('resolved')) return 'green';
  if (lower.includes('progress') || lower.includes('andamento')) return 'orange';
  return 'default';
};

const Step4Summary: FC<IStep4SummaryProps> = ({
  fileAnalysisResult,
  mappingConfigs,
  importSettings,
  isLoading,
  error
}) => {
  const { t } = useTranslation('activities');

  // Calculate mapping summary from current state
  const mappingSummary = useMemo(() => {
    if (!mappingConfigs || !fileAnalysisResult?.bcfValues) {
      return { fieldsWithValues: [], totalBcfValues: 0, totalMapped: 0, completeness: 0 };
    }

    const fields = Object.entries(mappingConfigs).map(([key, config]: [string, any]) => {
      const bcfValuesCount = config.bcfValues?.length || 0;
      const mappedCount = Object.keys(config.mappingRules || {}).length;
      return {
        key,
        label: FieldLabels[key] || key,
        bcfValuesCount,
        mappedCount,
        isComplete: bcfValuesCount === 0 || mappedCount >= bcfValuesCount,
      };
    });

    const fieldsWithValues = fields.filter(f => f.bcfValuesCount > 0);
    const totalBcfValues = fieldsWithValues.reduce((sum, f) => sum + f.bcfValuesCount, 0);
    const totalMapped = fieldsWithValues.reduce((sum, f) => sum + f.mappedCount, 0);
    const completeness = totalBcfValues > 0 ? Math.round((totalMapped / totalBcfValues) * 100) : 100;

    return { fieldsWithValues, totalBcfValues, totalMapped, completeness };
  }, [mappingConfigs, fileAnalysisResult]);

  const topics: ITopicPreview[] = useMemo(() => {
    return fileAnalysisResult?.bcfValues?.topics || [];
  }, [fileAnalysisResult]);

  if (!fileAnalysisResult) {
    return (
      <Container>
        <Empty description="Nenhum arquivo analisado" />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Resumo da Importação</Title>
        <Description>Revise os dados antes de confirmar a importação</Description>
      </Header>

      {error && <Alert message={error} type="error" showIcon />}

      <CardGrid>
        <Card>
          <CardLabel>📁 Total de Tópicos</CardLabel>
          <CardValue>{fileAnalysisResult.bcfValues?.totalTopics || 0}</CardValue>
        </Card>

        <Card>
          <CardLabel>📊 Mapeados</CardLabel>
          <CardValue>{mappingSummary.totalMapped} / {mappingSummary.totalBcfValues}</CardValue>
        </Card>

        <Card style={{
          borderLeftColor: fileAnalysisResult.bcfValues?.totalViewpoints > 0 ? '#10b981' : '#9ca3af'
        }}>
          <CardLabel>📷 Viewpoints</CardLabel>
          <CardValue style={{ color: fileAnalysisResult.bcfValues?.totalViewpoints > 0 ? '#10b981' : '#9ca3af' }}>
            {fileAnalysisResult.bcfValues?.totalViewpoints || 0}
          </CardValue>
        </Card>
      </CardGrid>

      {/* Topics List */}
      {topics.length > 0 && (
        <TopicsSection>
          <SectionTitle>Tópicos a Importar ({topics.length})</SectionTitle>
          <CollapsibleContent>
            <Collapse
              defaultActiveKey={topics.length <= 5 ? ['topics'] : []}
              items={[{
                key: 'topics',
                label: `Ver ${topics.length} tópico${topics.length !== 1 ? 's' : ''}`,
                children: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px' }}>
                    {topics.map((topic, index) => (
                      <TopicCard key={topic.guid || index}>
                        <TopicHeader>
                          <TopicTitle>
                            <FileTextOutlined style={{ marginRight: 8 }} />
                            {topic.title}
                          </TopicTitle>
                          <TopicMeta>
                            {topic.topicStatus && (
                              <Tag color={getStatusColor(topic.topicStatus)}>{topic.topicStatus}</Tag>
                            )}
                            {topic.topicType && (
                              <Tag color="blue">{topic.topicType}</Tag>
                            )}
                            {topic.priority && (
                              <Tag color={getPriorityColor(topic.priority)}>{topic.priority}</Tag>
                            )}
                            {topic.viewpointCount > 0 && (
                              <Badge count={topic.viewpointCount} style={{ backgroundColor: '#10b981' }}>
                                <CameraOutlined style={{ fontSize: 16, color: '#666' }} />
                              </Badge>
                            )}
                          </TopicMeta>
                        </TopicHeader>

                        {topic.description && (
                          <TopicDescription>{topic.description}</TopicDescription>
                        )}

                        <TopicDetails>
                          {topic.creationAuthor && (
                            <DetailItem>
                              <UserOutlined />
                              Criado por: {topic.creationAuthor}
                            </DetailItem>
                          )}
                          {topic.assignedTo && (
                            <DetailItem>
                              <UserOutlined />
                              Atribuído a: {topic.assignedTo}
                            </DetailItem>
                          )}
                          {topic.dueDate && (
                            <DetailItem>
                              <CalendarOutlined />
                              Prazo: {topic.dueDate}
                            </DetailItem>
                          )}
                        </TopicDetails>

                        {topic.labels && topic.labels.length > 0 && (
                          <LabelsList>
                            {topic.labels.map((label, i) => (
                              <Tag key={i} style={{ fontSize: 10 }}>{label}</Tag>
                            ))}
                          </LabelsList>
                        )}
                      </TopicCard>
                    ))}
                  </div>
                )
              }]}
            />
          </CollapsibleContent>
        </TopicsSection>
      )}

      {mappingSummary.fieldsWithValues.length > 0 && (
        <MappingSummarySection>
          <SectionTitle>Mapeamentos por Campo</SectionTitle>
          {mappingSummary.fieldsWithValues.map((field) => (
            <MappingRow key={field.key}>
              <MappingLabel>{field.label}</MappingLabel>
              <MappingStatus complete={field.isComplete}>
                {field.isComplete ? <CheckCircleOutlined /> : <WarningOutlined />}
                {field.mappedCount} / {field.bcfValuesCount}
              </MappingStatus>
            </MappingRow>
          ))}
        </MappingSummarySection>
      )}

      <SettingsSummary>
        <SectionTitle>Configurações</SectionTitle>
        <SettingRow>
          <SettingLabel>Atualizar existentes:</SettingLabel>
          <SettingValue>{importSettings?.updateExistingIssues ? 'Sim' : 'Não'}</SettingValue>
        </SettingRow>
        <SettingRow>
          <SettingLabel>{t('bcfImport.createNewIssuesSummary')}</SettingLabel>
          <SettingValue>{importSettings?.createNewActivities ? 'Sim' : 'Não'}</SettingValue>
        </SettingRow>
        <SettingRow>
          <SettingLabel>Anexar arquivos:</SettingLabel>
          <SettingValue>{importSettings?.attachFiles ? 'Sim' : 'Não'}</SettingValue>
        </SettingRow>
      </SettingsSummary>

      <Alert
        message="Pronto para importar"
        description="Clique em 'Próximo' para iniciar a importação."
        type="info"
        showIcon
      />
    </Container>
  );
};

export default Step4Summary;
